import { useState } from "react";

const WHATSAPP_NUMBER = "17863575321";

export default function FinalCTA() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    package: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");

    const lines = [
      "Hello! I'd like to start my website project. Here are my details:",
      "",
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      formData.phone ? `Phone: ${formData.phone}` : null,
      formData.business ? `Business: ${formData.business}` : null,
      formData.package ? `Package of Interest: ${formData.package}` : null,
      formData.message ? `\nAbout My Business:\n${formData.message}` : null,
    ].filter(Boolean).join("\n");

    const encoded = encodeURIComponent(lines);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;

    window.open(url, "_blank", "noopener,noreferrer");
    setStatus("success");
    setFormData({ name: "", email: "", phone: "", business: "", package: "", message: "" });
  };

  return (
    <section id="cta" className="bg-[#070d1a] py-24 md:py-32 relative overflow-hidden">
      {/* Gold grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      {/* Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-48 opacity-20 blur-3xl bg-[#D4AF37] rounded-full" />
      <div className="absolute bottom-0 right-10 w-80 h-80 opacity-10 blur-3xl bg-[#D4AF37] rounded-full" />

      {/* W2R Watermark */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
        aria-hidden
      >
        <span
          className="text-[#D4AF37]/5 font-black select-none"
          style={{ fontSize: "clamp(120px, 20vw, 260px)", fontFamily: "'Montserrat', sans-serif", letterSpacing: "-10px" }}
        >
          W2R
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        {/* Headline */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-5">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Ready to Grow?
            </span>
            <div className="h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2
            className="text-white font-black leading-tight mb-5"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "clamp(40px, 6vw, 80px)",
              letterSpacing: "-2px",
            }}
          >
            Ready to Grow
            <br />
            <span
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #f5d980 50%, #D4AF37 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Your Business?
            </span>
          </h2>
          <p className="text-white/50 text-lg max-w-lg mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
            Let&apos;s build your revenue-generating website. Fill out the form or reach out directly — we&apos;ll respond within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <div className="bg-white/5 border border-[#D4AF37]/20 rounded-2xl p-8 md:p-10">
            <h3 className="text-white font-bold text-xl mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Start My Website
            </h3>
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D4AF37]/20">
                  <i className="ri-check-line text-[#D4AF37] text-3xl" />
                </div>
                <h4 className="text-white font-bold text-lg" style={{ fontFamily: "'Montserrat', sans-serif" }}>Opening WhatsApp!</h4>
                <p className="text-white/50 text-sm text-center" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Your info is ready. Just hit send in WhatsApp and we&apos;ll get back to you shortly!
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-4 text-[#D4AF37] text-sm underline cursor-pointer"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-white/60 text-xs tracking-wider uppercase block mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="w-full bg-white/5 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-xs tracking-wider uppercase block mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full bg-white/5 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-white/60 text-xs tracking-wider uppercase block mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Phone / WhatsApp
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (305) 000-0000"
                      className="w-full bg-white/5 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-xs tracking-wider uppercase block mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Business Name
                    </label>
                    <input
                      type="text"
                      name="business"
                      value={formData.business}
                      onChange={handleChange}
                      placeholder="My Business LLC"
                      className="w-full bg-white/5 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-white/60 text-xs tracking-wider uppercase block mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Package Interest
                  </label>
                  <select
                    name="package"
                    value={formData.package}
                    onChange={handleChange}
                    className="w-full bg-[#0A1628] border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300 cursor-pointer"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <option value="">Select a package...</option>
                    <option value="Starter Website - $247">Starter Website — $247</option>
                    <option value="Growth Website - $497">Growth Website — $497</option>
                    <option value="Premium Revenue System - $797-$997">Premium Revenue System — $797–$997</option>
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
                <div>
                  <label className="text-white/60 text-xs tracking-wider uppercase block mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Tell Us About Your Business
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={4}
                    placeholder="Describe your business and what you're looking to achieve..."
                    className="w-full bg-white/5 border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300 resize-none"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                  <p className="text-white/20 text-xs mt-1 text-right" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {formData.message.length}/500
                  </p>
                </div>
                {status === "error" && (
                  <p className="text-red-400 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Something went wrong. Please try again or contact us via WhatsApp.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full bg-[#D4AF37] hover:bg-[#c4a030] disabled:opacity-60 text-[#070d1a] font-black text-sm py-4 rounded-full tracking-wider uppercase transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-[#D4AF37]/20 hover:shadow-[#D4AF37]/40 hover:scale-[1.02]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {status === "sending" ? "Opening WhatsApp..." : "Send via WhatsApp"}
                </button>
              </form>
            )}
          </div>

          {/* Right Side Info */}
          <div className="flex flex-col gap-8">
            {/* Social proof */}
            <div className="bg-white/5 border border-[#D4AF37]/20 rounded-2xl p-7">
              <p className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Social Proof
              </p>
              <div className="flex flex-col gap-5">
                {[
                  { quote: "My barbershop went from 10 bookings a week to fully booked every day within 2 months!", name: "Carlos M.", role: "Barbershop Owner, Miami" },
                  { quote: "Web2Revenue built me a website that actually brings clients. Best investment I&apos;ve made.", name: "Sofia R.", role: "Beauty Studio Owner" },
                  { quote: "Before this, nobody could find my restaurant online. Now I&apos;m turning people away!", name: "Marco T.", role: "Restaurant Owner, Miami" },
                ].map((t, i) => (
                  <div key={i} className="border-l-2 border-[#D4AF37]/40 pl-5">
                    <p className="text-white/70 text-sm italic leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      &ldquo;{t.quote.replace(/&apos;/g, "'")}&rdquo;
                    </p>
                    <div>
                      <p className="text-white font-semibold text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t.name}</p>
                      <p className="text-white/40 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{t.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Direct Contact */}
            <div className="bg-white/5 border border-[#D4AF37]/20 rounded-2xl p-7">
              <p className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Reach Us Directly
              </p>
              <div className="flex flex-col gap-4">
                <a
                  href="https://wa.me/17863575321"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 bg-[#25D366]/10 border border-[#25D366]/30 rounded-xl p-4 hover:bg-[#25D366]/20 transition-colors duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#25D366]/20 shrink-0">
                    <i className="ri-whatsapp-line text-[#25D366] text-xl" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>WhatsApp</p>
                    <p className="text-white/40 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>+1 (786) 357-5321</p>
                  </div>
                  <i className="ri-arrow-right-line text-white/30 ml-auto" />
                </a>
                <a
                  href="https://tiktok.com/@web2revenue"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 shrink-0">
                    <i className="ri-tiktok-line text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>TikTok</p>
                    <p className="text-white/40 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>@web2revenue</p>
                  </div>
                  <i className="ri-arrow-right-line text-white/30 ml-auto" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
